import './Tooltip.scss';
import React, {
    createContext,
    cloneElement,
    CSSProperties,
    FC,
    forwardRef,
    HTMLProps,
    isValidElement,
    ReactNode,
    useContext,
    useMemo,
    useState,
} from 'react';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    useMergeRefs,
    FloatingPortal,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import classNames from 'classnames';

interface TooltipOptions {
    initialOpen?: boolean;
    placement?: Placement;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function useTooltip({
    initialOpen = false,
    placement = 'top',
    open: controlledOpen,
    onOpenChange: setControlledOpen,
}: TooltipOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'start',
                padding: 5,
            }),
            shift({ padding: 5 }),
        ],
    });

    const context = data.context;

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null,
        restMs: 200,
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });

    const interactions = useInteractions([hover, focus, dismiss, role]);

    return useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
        }),
        [open, setOpen, interactions, data],
    );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipContext = () => {
    const context = useContext(TooltipContext);

    if (context == null) {
        throw new Error(
            'Tooltip components must be wrapped in <TooltipWrapper />',
        );
    }

    return context;
};

export function TooltipWrapper({
    children,
    ...options
}: { children: ReactNode } & TooltipOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip(options);
    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    );
}
interface TooltipTriggerProps {
    children: ReactNode;
}

export const TooltipTrigger = forwardRef<
    HTMLElement,
    HTMLProps<HTMLElement> & TooltipTriggerProps
>(function TooltipTrigger({ children, ...props }, propRef) {
    const context = useTooltipContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    if (isValidElement(children)) {
        return cloneElement(
            children,
            context.getReferenceProps({
                ref,
                ...props,
                ...children.props,
                'data-state': context.open ? 'open' : 'closed',
            }),
        );
    }

    // TODO: check if this is at all possible.
    console.error(
        'Should never enter here in TooltipTrigger of TooltipBase.tsx',
    );
    return (
        <button
            ref={ref}
            // The user can style the trigger based on the state
            data-state={context.open ? 'open' : 'closed'}
            {...context.getReferenceProps(props)}
        >
            {children}
        </button>
    );
});

interface TooltipContentProps {
    style?: CSSProperties;
    className?: string;
}

export const TooltipContent = forwardRef<
    HTMLDivElement,
    HTMLProps<HTMLDivElement> & TooltipContentProps
>(function TooltipContent({ style, className, ...props }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!context.open) return null;

    return (
        <FloatingPortal>
            <div
                ref={ref}
                style={{
                    ...context.floatingStyles,
                    ...style,
                }}
                {...context.getFloatingProps(props)}
                className={classNames('Tooltip', className)}
            />
        </FloatingPortal>
    );
});

type TooltipProps = {
    children: ReactNode;
    content: ReactNode | string | number;
    isControlled?: boolean;
    isOpen?: boolean;
    className?: string;
};

const Uncontrolled: FC<TooltipProps> = ({ children, content, className }) => {
    return (
        <TooltipWrapper>
            <TooltipTrigger>{children}</TooltipTrigger>
            <TooltipContent className={className}>{content}</TooltipContent>
        </TooltipWrapper>
    );
};

const Controlled: FC<TooltipProps> = ({
    children,
    content,
    isOpen = false,
    className,
}) => {
    const [open, setOpen] = useState(isOpen);

    return (
        <TooltipWrapper open={open} onOpenChange={setOpen}>
            <TooltipTrigger onClick={() => setOpen((v) => !v)}>
                {children}
            </TooltipTrigger>
            <TooltipContent className={className}>{content}</TooltipContent>
        </TooltipWrapper>
    );
};

export default function Tooltip({
    children,
    content,
    isControlled, // false = hover, true = onclick
    isOpen, // true: always isControlled and auto opens when mounted.
    className,
}: TooltipProps) {
    if (isControlled || isOpen) {
        return (
            <Controlled content={content} isOpen={isOpen} className={className}>
                {children}
            </Controlled>
        );
    }

    return (
        <Uncontrolled content={content} className={className}>
            {children}
        </Uncontrolled>
    );
}
